import { LinkExternal01Icon } from '@langchain/untitled-ui-icons';
import { Button, LinearProgress } from '@mui/joy';
import { PaginationState, SortingState } from '@tanstack/react-table';

import { Copy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { DEFAULT_10_PAGINATION_MODEL } from '@/constants/dataGridConstants';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { useComparativeExperiments } from '@/hooks/useSwr';

import EmptyState from '../EmptyState';
import { ComparativeExperimentsDataGrid } from './ComparativeExperimentsDataGrid';

export const ComparativeExperimentsTable = (props: {
  datasetId?: string;
  datasetShareToken?: string;
}) => {
  // pagination and filtering state

  const [paginationState, setPaginationState] = useStateFromSearchParams(
    'paginationState',
    JSON.stringify(DEFAULT_10_PAGINATION_MODEL)
  );

  let pagination = DEFAULT_10_PAGINATION_MODEL;
  try {
    pagination = JSON.parse(paginationState ?? '') as PaginationState;
  } catch (e) {
    // ignore: use default
  }
  const setPagination = useCallback(
    (paginationUpdater) => {
      const newPagination = paginationUpdater(pagination);
      setPaginationState(JSON.stringify(newPagination));
    },
    [pagination, setPaginationState]
  );

  const [name, setName] = useState('');
  const [debounceName] = useDebounce(name, 500, { trailing: true });
  const [searchParams, setSearchParams] = useSearchParams();

  let sortingState: SortingState;
  try {
    sortingState = JSON.parse(searchParams.get('sortingState') ?? '[]');
  } catch (e) {
    sortingState = [];
  }
  if (sortingState.length === 0) {
    sortingState = [{ id: 'created_at', desc: true }];
  }
  const setSortingState = (sortingStateUpdater) => {
    const newSortingState = sortingStateUpdater(sortingState);
    setSearchParams(
      (prev) => {
        prev.set('sortingState', JSON.stringify(newSortingState));
        return prev;
      },
      { replace: true }
    );
  };
  const sortingColumn = debounceName ? undefined : sortingState[0].id;
  const isSortingDesc = sortingState[0].desc;

  const comparativeExperiments = useComparativeExperiments(
    props.datasetId ?? null,
    {
      name_contains: debounceName ? debounceName : undefined,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      sort_by: sortingColumn,
      sort_by_desc: isSortingDesc,
    },
    {
      datasetShareToken: props.datasetShareToken,
    }
  );

  if (!comparativeExperiments.data) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  return (
    <ComparativeExperimentsDataGrid
      comparativeExperiments={comparativeExperiments.data}
      datasetId={props.datasetId}
      pagination={pagination}
      setPagination={setPagination}
      sortingState={sortingState}
      handleSortingChange={setSortingState}
      datasetShareToken={props.datasetShareToken}
      name={name}
      setName={setName}
      isLoading={comparativeExperiments.isLoading}
      total={comparativeExperiments.data.headers?.['x-pagination-total'] ?? 0}
      emptyState={
        <div className="flex min-h-[600px] flex-col items-center justify-center gap-8">
          <EmptyState
            icon={<Copy />}
            title="Set up a Pairwise Experiment"
            description="Directly compare outputs from multiple experiments"
          />

          <a
            href="https://docs.langchain.com/langsmith/evaluate-pairwise"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="solid"
              color="primary"
              endDecorator={<LinkExternal01Icon className="size-4" />}
            >
              Docs
            </Button>
          </a>
        </div>
      }
    />
  );
};
